<script setup lang="ts">
import { useConfirm } from 'primevue/useconfirm';
import { formatCurrency } from 'src/utils/currency';
import type { NestedArticleOptionResponse } from 'src/api/models/nestedArticleOptionResponse';

interface Props {
  options: NestedArticleOptionResponse[];
  preferredOptionId?: string | null;
  disabled?: boolean;
}

const { options, preferredOptionId = null, disabled = false } = defineProps<Props>();

const emit = defineEmits<{
  'add-option': [];
  'remove-option': [optionId: string];
  'set-preferred': [optionId: string];
  'edit-option': [optionId: string];
}>();

const confirm = useConfirm();

function onRemove (opt: NestedArticleOptionResponse) {
  if (!opt.id) return;
  if (opt.id === preferredOptionId) {
    confirm.require({
      message: 'Cannot remove the preferred option. Switch to another option first.',
      header: 'Cannot remove',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'OK',
      acceptLabel: ' ',
      acceptClass: 'hidden',
    });
    return;
  }
  confirm.require({
    message: `Remove article option "${opt.brand_name ?? opt.external_reference ?? opt.id}"?`,
    header: 'Confirm removal',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: () => emit('remove-option', opt.id!),
  });
}

function onSetPreferred (opt: NestedArticleOptionResponse) {
  if (!opt.id || opt.id === preferredOptionId) return;
  emit('set-preferred', opt.id);
}
</script>

<template>
  <div data-testid="article-options-section" >
    <DataTable
      data-testid="options-table"
      :value="options"
      size="small"
      class="mb-3"
    >
      <template #empty>
        <div data-testid="options-empty" class="text-center py-4 text-color-secondary">
          No article options
        </div>
      </template>

      <Column header="" style="width: 40px">
        <template #body="{ data }">
          <Button
            v-if="data.id === preferredOptionId"
            data-testid="option-preferred-icon"
            icon="pi pi-star-fill"
            text
            size="small"
            severity="warn"
            class="p-0 w-6 h-6"
            v-tooltip.top="'Preferred option'"
          />
          <Button
            v-else
            data-testid="option-set-preferred-btn"
            icon="pi pi-star"
            text
            size="small"
            severity="secondary"
            class="p-0 w-6 h-6"
            v-tooltip.top="'Set as preferred'"
            :disabled="disabled"
            @click="onSetPreferred(data)"
          />
        </template>
      </Column>

      <Column field="brand_name" header="Brand" >
        <template #body="{ data }">
          <span data-testid="option-brand">{{ data.brand_name }}</span>
        </template>
      </Column>

      <Column field="external_reference" header="Reference">
        <template #body="{ data }">
          <span data-testid="option-external-ref" class="font-mono text-sm">{{ data.external_reference || '-' }}</span>
        </template>
      </Column>

      <Column header="Supplier">
        <template #body="{ data }">
          <span data-testid="option-supplier">{{ data.supplier?.name || '-' }}</span>
        </template>
      </Column>

      <Column header="Gross" >
        <template #body="{ data }">
          <span data-testid="option-gross-price" class="font-mono text-sm">{{ formatCurrency(data.purchase_gross_price ?? 0) }}</span>
        </template>
      </Column>

      <Column header="Discount" >
        <template #body="{ data }">
          <span data-testid="option-discount" class="font-mono text-sm">{{ data.purchase_discount ?? 0 }}%</span>
        </template>
      </Column>

      <Column header="Net" >
        <template #body="{ data }">
          <span data-testid="option-net-price" class="font-mono text-sm">{{ formatCurrency(data.purchase_net_price ?? 0) }}</span>
        </template>
      </Column>


      <Column header="" style="width: 80px">
        <template #body="{ data }">
          <div class="flex gap-1">
            <Button
              data-testid="option-edit-btn"
              icon="pi pi-pencil"
              severity="secondary"
              text
              size="small"
              v-tooltip.top="'Edit option'"
              :disabled="disabled"
              @click="emit('edit-option', data.id)"
            />
            <Button
              data-testid="option-remove-btn"
              icon="pi pi-trash"
              severity="danger"
              text
              size="small"
              :disabled="disabled"
              @click="onRemove(data)"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <Button
      data-testid="option-add-btn"
      label="Add option"
      icon="pi pi-plus"
      severity="secondary"
      size="small"
      outlined
      :disabled="disabled"
      class="mt-2"
      @click="emit('add-option')"
    />
  </div>
</template>
